import { ReactNode } from 'react'

// Base skeleton element component
export const SkeletonElement = ({
  className = '',
  width = 'w-full',
  height = 'h-4',
  rounded = 'rounded',
  opacity = '!bg-gray-800/40',
  animate = true,
  style,
}: {
  className?: string
  width?: string
  height?: string
  rounded?: string
  opacity?: string
  animate?: boolean
  style?: React.CSSProperties
}) => (
  <div
    className={`${width} ${height} ${opacity} ${rounded} ${animate ? 'animate-pulse' : ''} ${className}`}
    style={style}
  />
)

// Container component for grouped skeletons
export const SkeletonGroup = ({
  children,
  className = '',
  gap = 'gap-2',
  direction = 'flex',
}: {
  children: ReactNode
  className?: string
  gap?: string
  direction?: 'flex' | 'flex-col' | 'grid'
}) => <div className={`${direction} ${gap} ${className}`}>{children}</div>

// Preset skeleton shapes with better defaults
export const SkeletonCircle = ({
  size = '7',
  className = '',
  ...props
}: Omit<Parameters<typeof SkeletonElement>[0], 'width' | 'height'> & {
  size?: string
}) => (
  <SkeletonElement
    width={`w-${size}`}
    height={`h-${size}`}
    className={`border-2 !border-gray-800 ${className}`}
    rounded="rounded-full"
    opacity="!bg-gray-800/40"
    {...props}
  />
)

export const SkeletonBar = ({
  width = '20',
  height = '4',
  className = '',
  ...props
}: Omit<Parameters<typeof SkeletonElement>[0], 'width' | 'height'> & {
  width?: string
  height?: string
}) => <SkeletonElement width={`w-${width}`} height={`h-${height}`} className={className} {...props} />

export const SkeletonButton = ({
  width = '16',
  height = '8',
  className = '',
  ...props
}: Omit<Parameters<typeof SkeletonElement>[0], 'width' | 'height'> & {
  width?: string
  height?: string
}) => (
  <SkeletonElement
    width={`w-${width}`}
    height={`h-${height}`}
    className={className}
    rounded="rounded-full"
    opacity="!bg-gray-800/40"
    {...props}
  />
)

// Updated components using the new system
export const TABLE_ROW_HEIGHT = 61
export const POOLS_TABLE_ROW_HEIGHT = TABLE_ROW_HEIGHT
export const TOKENS_TABLE_ROW_HEIGHT = TABLE_ROW_HEIGHT

export const PoolsTableSkeletonRow = () => (
  <tr className="text-kodiak-text-primary text-sm md:text-base align-middle" style={{ height: POOLS_TABLE_ROW_HEIGHT }}>
    <td className="px-2 py-3 md:p-4 text-center w-12 md:w-16">
      <div className="flex justify-center">
        <SkeletonBar width="6" height="3" className="md:w-8" />
      </div>
    </td>
    <td className="px-2 py-3 min-w-45 md:p-4 md:w-[290px]">
      <div className="flex items-center gap-2 md:gap-3 md:w-[290px]">
        <div className="flex -space-x-2">
          <SkeletonCircle size="7" />
          <SkeletonCircle size="7" />
        </div>
        <div className="flex flex-col md:flex-row md:items-center gap-1.5 md:gap-2 max-w-[290px]">
          <SkeletonBar width="full" height="4" className="max-w-[110px] md:max-w-[160px]" />
          <SkeletonElement width="w-14 md:w-16" height="h-3 md:h-4" />
        </div>
      </div>
    </td>
    <td className="px-2 py-3 md:p-4 text-right">
      <div className="flex w-full justify-end">
        <SkeletonBar width="full" height="4" className="max-w-[96px] md:max-w-[120px]" />
      </div>
    </td>

    <td className="px-2 py-3 md:p-4 text-right">
      <div className="flex w-full justify-end">
        <SkeletonBar width="full" height="4" className="max-w-[96px] md:max-w-[120px]" />
      </div>
    </td>
    <td className="px-2 py-3 md:p-4 text-right">
      <div className="flex w-full justify-end">
        <SkeletonBar width="full" height="4" className="max-w-[96px] md:max-w-[120px]" />
      </div>
    </td>
    <td className="px-2 py-3 md:p-4 text-right">
      <div className="flex w-full justify-end">
        <SkeletonBar width="full" height="4" className="max-w-[96px] md:max-w-[120px]" />
      </div>
    </td>
    <td className="px-2 py-3 md:p-4 text-right">
      <div className="flex w-full justify-end">
        <SkeletonBar width="full" height="4" className="max-w-[96px] md:max-w-[120px]" />
      </div>
    </td>
    <td className="px-2 py-3 md:p-4 text-right">
      <div className="flex w-full justify-end">
        <SkeletonBar width="full" height="4" className="max-w-[80px] md:max-w-[96px]" />
      </div>
    </td>
  </tr>
)

export const TokensTableSkeletonRow = () => (
  <tr
    className="text-kodiak-text-primary text-sm md:text-base align-middle"
    style={{ height: TOKENS_TABLE_ROW_HEIGHT }}
  >
    <td className="px-2 py-3 md:p-4 text-center w-12 md:w-16">
      <div className="flex justify-center">
        <SkeletonBar width="6" height="3" className="md:w-8" />
      </div>
    </td>
    <td className="px-2 py-3 md:p-4 min-w-45 md:w-[280px]">
      <div className="flex items-center gap-2 md:gap-3">
        <SkeletonCircle size="7" className="md:w-8 md:h-8" />
        <div className="flex flex-col gap-0.5 w-full">
          <SkeletonBar height="4" className="w-full max-w-[110px] md:max-w-[160px]" />
          <SkeletonBar height="3" className="w-full max-w-[80px] md:max-w-[100px]" />
        </div>
      </div>
    </td>
    <td className="px-2 py-3 md:p-4 text-right">
      <div className="flex w-full justify-end">
        <SkeletonBar width="full" height="4" className="max-w-[88px] md:max-w-[112px]" />
      </div>
    </td>
    <td className="px-2 py-3 min-w-[70px] md:p-4 text-right">
      <div className="flex w-full justify-end">
        <SkeletonBar width="full" height="4" className="max-w-[72px] md:max-w-[96px]" />
      </div>
    </td>
    <td className="px-2 py-3 min-w-[70px] md:p-4 text-right">
      <div className="flex w-full justify-end">
        <SkeletonBar width="full" height="4" className="max-w-[72px] md:max-w-[96px]" />
      </div>
    </td>
    <td className="px-2 py-3 md:p-4 text-right md:w-[125px]">
      <div className="flex w-full justify-end">
        <SkeletonBar width="full" height="4" className="max-w-[104px] md:max-w-[128px]" />
      </div>
    </td>
    <td className="px-2 py-3 md:p-4 text-right">
      <div className="flex w-full justify-end">
        <SkeletonBar width="full" height="4" className="max-w-[120px] md:max-w-[144px]" />
      </div>
    </td>
    <td className="px-2 py-3 md:p-4 text-right">
      <div className="flex w-full justify-end">
        <SkeletonBar width="full" height="4" className="max-w-[120px] md:max-w-[144px]" />
      </div>
    </td>
  </tr>
)

export const ChartSkeleton = ({
  className,
  hasManageBar,
  manageBarItems = 3,
}: {
  className?: string
  hasManageBar?: boolean
  manageBarItems?: number
}) => (
  <div className="flex flex-col gap-4 w-full">
    {hasManageBar && (
      <div className="w-full">
        <div
          className="flex gap-1 bg-kodiak-card-tertiary px-1 py-1 rounded-md w-full"
          role="group"
          aria-label="Transaction type filter loading"
        >
          {Array.from({ length: manageBarItems }).map((_, index) => (
            <div key={index} className="flex-1">
              <SkeletonElement className="h-9 w-full" rounded="rounded-sm" />
            </div>
          ))}
        </div>
      </div>
    )}
    <div
      className={`p-4 flex flex-col bg-white/5 dark:bg-gray-900 rounded-lg border border-gray-800  ${className || ''} `}
    >
      <SkeletonGroup direction="flex-col" gap="space-y-3" className="mb-6">
        <SkeletonBar width="16" height="3" opacity="bg-gray-800/50" />
        <SkeletonBar width="28" height="6" opacity="bg-gray-800/60" />
        <SkeletonBar width="32" height="3" opacity="bg-gray-800/40" />
      </SkeletonGroup>
      <div className="flex-1">
        <SkeletonElement opacity="bg-gray-800/40" className="h-full" />
      </div>
    </div>
  </div>
)

export const AssetHeaderSkeleton = () => (
  <div className="flex justify-between gap-3">
    <div className="flex gap-2 items-center">
      <SkeletonGroup className="items-center" gap="gap-2">
        <SkeletonGroup className="-space-x-3" direction="flex">
          <SkeletonCircle className="w-7 h-7" />
        </SkeletonGroup>
        <SkeletonBar className="rounded-md h-7 w-8" />
        {/* <SkeletonBar  className="rounded-md" /> */}
        {/* <SkeletonButton className="w-10 h-10" /> */}
      </SkeletonGroup>
    </div>
    <SkeletonCircle className="rounded-xl w-7 h-7" />
  </div>
)

export const DetailsLinksSkeleton = ({ rows = 3 }: { rows?: number }) => (
  <div className="w-full rounded-lg bg-kodiak-bg-floating flex flex-col gap-4 py-5 px-4">
    <SkeletonBar width="16" height="6" />

    <div className="flex flex-col gap-2">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="flex justify-between gap-2 min-h-10">
          <SkeletonGroup className="items-center" gap="gap-2">
            <SkeletonCircle size="7" />
            <SkeletonBar className="w-24 h-4" />
          </SkeletonGroup>

          <SkeletonGroup className="items-center" gap="gap-2">
            <SkeletonBar className="w-24 h-5" />
            <SkeletonCircle size="6" />
          </SkeletonGroup>
        </div>
      ))}
    </div>
  </div>
)

export const TransactionsTableSkeleton = ({ rows = 8 }: { rows?: number }) => (
  <div className="w-full rounded-lg border border-gray-800 bg-gray-900/50 flex flex-col">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="border-b border-gray-800">
          <tr className="text-gray-400 text-sm uppercase tracking-wide">
            <th className="text-left px-4 py-3 font-medium w-[6rem]">
              <SkeletonBar width="16" height="4" className="mx-auto" />
            </th>
            <th className="text-left px-4 py-3 font-medium">
              <SkeletonBar width="16" height="4" />
            </th>
            <th className="text-right px-4 py-3 font-medium w-[10rem]">
              <SkeletonBar width="16" height="4" className="ml-auto" />
            </th>
            <th className="text-right px-4 py-3 font-medium w-[10rem]">
              <SkeletonBar width="16" height="4" className="ml-auto" />
            </th>
            <th className="text-right px-4 py-3 font-medium w-[10rem]">
              <SkeletonBar width="16" height="4" className="ml-auto" />
            </th>
            <th className="text-right px-4 py-3 font-medium w-[12rem]">
              <SkeletonBar width="20" height="4" className="ml-auto" />
            </th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, index) => (
            <tr key={index} className="border-b border-gray-800/60">
              <td className="px-4 py-3">
                <SkeletonBar width="16" height="4" />
              </td>
              <td className="px-4 py-3">
                <SkeletonBar width="20" height="4" />
              </td>
              <td className="px-4 py-3 text-right">
                <SkeletonBar width="20" height="4" className="ml-auto" />
              </td>
              <td className="px-4 py-3 text-right">
                <SkeletonBar width="24" height="4" className="ml-auto" />
              </td>
              <td className="px-4 py-3 text-right">
                <SkeletonBar width="24" height="4" className="ml-auto" />
              </td>
              <td className="px-4 py-3 text-right">
                <SkeletonBar width="24" height="4" className="ml-auto" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className="border-t border-gray-800 px-4 py-3 flex justify-end">
      <SkeletonBar width="24" height="5" className="ml-auto" />
    </div>
  </div>
)
